import { useTranslation } from 'react-i18next';
import type {
  EnergySurchargeBreakdown,
  PricingBreakdown,
  PricingSiteCosts,
} from '@carrierbridge/shared';
import { Copy, AlertTriangle, Clock, FileText, Check, Info } from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatEUR, formatNumber, formatPercent } from '@/lib/utils';
import { useToast } from '@/components/ui/Toast';
import { useMarginsEnabled } from '@/hooks/useSettings';
import { PriceWaterfall } from './PriceWaterfall';

interface BreakdownDrawerProps {
  open: boolean;
  onClose: () => void;
  breakdown: PricingBreakdown | null;
}

export function BreakdownDrawer({ open, onClose, breakdown }: BreakdownDrawerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  if (!breakdown) return null;

  // ZONE = matching précis (zone géographique dédiée), pas un repli.
  const isFallback = breakdown.laneFallback && breakdown.laneFallback !== 'DEPARTMENT' && breakdown.laneFallback !== 'EXACT' && breakdown.laneFallback !== 'ZONE';
  const isPaletteConversion = breakdown.convertedQuantity != null;

  // Categorized ancillary results
  const appliedCharges = breakdown.ancillaryAppliedCharges || [];
  const includedItems = breakdown.ancillaryIncludedItems || [];
  const potentialEvents = breakdown.ancillaryPotentialEvents || [];
  const invoiceItems = breakdown.ancillaryInvoice || [];
  const notAppliedInfo = breakdown.ancillaryNotAppliedInfo || [];
  const missingInput = breakdown.ancillaryNotAppliedMissingInput || [];

  const hasCategories = appliedCharges.length > 0 || includedItems.length > 0 || potentialEvents.length > 0 || invoiceItems.length > 0 || notAppliedInfo.length > 0 || missingInput.length > 0;
  const flatAncillaries = !hasCategories ? (breakdown.ancillaries || []) : [];

  function buildTextSummary(): string {
    if (!breakdown) return '';
    const lines: string[] = [];
    lines.push(`${t('common.carrier')}: ${breakdown.carrierName} (${breakdown.carrierCode})`);
    lines.push(`${t('breakdown.total')}: ${formatEUR(breakdown.totalPrice)}`);
    lines.push(
      `${t('breakdown.pricePerUnit', { unit: breakdown.requestedUnit || t('common.unit') })}: ${formatEUR(breakdown.pricePerUnit)}`,
    );
    lines.push('');
    lines.push(`--- ${t('breakdown.laneAndBand')} ---`);
    lines.push(`${t('breakdown.laneKey')}: ${breakdown.laneKey}`);
    lines.push(`${t('breakdown.match')}: ${breakdown.laneFallback}`);
    lines.push(`${t('breakdown.band')}: ${breakdown.bandMinQty}-${breakdown.bandMaxQty ?? '+'} ${breakdown.bandUnit || ''}`);
    if (breakdown.surcharges?.length > 0) {
      lines.push('');
      lines.push(`--- ${t('breakdown.appliedSurcharges')} ---`);
      for (const s of breakdown.surcharges) {
        const amt = s.ruleAmount ?? s.amount;
        const rate = s.surchargeType === 'PERCENT' ? `${amt}%` : s.surchargeType === 'PER_UNIT' ? `${formatEUR(amt)}/u` : formatEUR(amt);
        lines.push(`${s.ruleName}: ${rate} = ${formatEUR(s.computedAmount ?? s.computed)}`);
      }
    }
    if (appliedCharges.length > 0) {
      lines.push('');
      lines.push(`--- ${t('breakdown.appliedToQuote')} ---`);
      for (const a of appliedCharges) lines.push(`${a.ruleName}: +${formatEUR(a.delta)}`);
    }
    if (potentialEvents.length > 0) {
      lines.push('');
      lines.push(`--- ${t('breakdown.potentialEvents')} ---`);
      for (const a of potentialEvents) lines.push(`${a.ruleName}: ${t('breakdown.event')}`);
    }
    return lines.join('\n');
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(buildTextSummary());
      toast('success', t('breakdown.copied'));
    } catch {
      toast('error', t('breakdown.copyError'));
    }
  }

  const effectiveBandPrice = breakdown.payantPourApplied
    ? breakdown.payantPourBandPrice
    : breakdown.bandPrice;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`${breakdown.carrierName} - ${formatEUR(breakdown.totalPrice)}`}
      footer={
        <Button variant="outline" size="sm" onClick={handleCopy}>
          <Copy className="h-4 w-4" />
          {t('breakdown.copyDetail')}
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Lane & Tranche */}
        <section>
          <h3 className="mb-3 text-[13px] font-semibold text-text">{t('breakdown.laneAndBand')}</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between">
              <span className="text-subtle">{t('breakdown.laneKey')}</span>
              <span className="font-mono text-xs text-text">{breakdown.laneKey}</span>
            </div>
            {breakdown.gridLabel && (
              <div className="flex justify-between">
                <span className="text-subtle">{t('breakdown.scope')}</span>
                <Badge variant="neutral">{breakdown.gridLabel}</Badge>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-subtle">{t('breakdown.match')}</span>
              <Badge variant={isFallback ? 'warning' : 'success'}>{breakdown.laneFallback}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-subtle">{t('breakdown.band')}</span>
              <span className="font-mono text-xs text-text">
                {breakdown.bandMinQty}-{breakdown.bandMaxQty ?? '+'} {breakdown.bandUnit || ''}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-subtle">{t('breakdown.type')}</span>
              <Badge variant="info">{breakdown.pricingType === 'PER_UNIT' ? t('breakdown.perUnit') : t('breakdown.flatRate')}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-subtle">{t('breakdown.bandPrice')}</span>
              <span className="font-semibold text-text">
                {formatEUR(breakdown.bandPrice)}
                {breakdown.pricingType === 'PER_UNIT' && (
                  <span className="font-normal text-subtle">/{breakdown.bandUnit || 'u'}</span>
                )}
              </span>
            </div>
            {/* Tranche cotée dans les deux modes : sans cette ligne, le détail
                annoncerait « 27 t × 12,50 » sous un total de 309 €. */}
            {breakdown.altBasePrice != null && (
              <div className="rounded bg-success-soft/60 border border-success/20 p-3 text-xs text-success">
                {t('breakdown.altPriceKept', {
                  kept: formatEUR(breakdown.basePrice),
                  alternative: formatEUR(breakdown.altBasePrice),
                  altType: breakdown.altPricingType === 'PER_UNIT'
                    ? t('breakdown.perUnit')
                    : t('breakdown.flatRate'),
                })}
              </div>
            )}
          </div>
        </section>

        <div className="h-px bg-surface-subtle" />

        {isPaletteConversion && (
          <>
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">{t('breakdown.paletteConversion')}</h3>
              <div className="rounded bg-accent-soft/60 border border-accent/20 p-3.5 text-[13px] text-accent">
                {breakdown.requestedQuantity} {breakdown.requestedUnit} = {breakdown.convertedQuantity} {breakdown.bandUnit}
              </div>
            </section>
            <div className="h-px bg-surface-subtle" />
          </>
        )}

        {(breakdown.effectiveWeightKg != null || breakdown.effectivePaletteCount != null) && (
          <>
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">{t('breakdown.conversions')}</h3>
              <div className="space-y-2.5 text-sm">
                {breakdown.effectiveWeightKg != null && (
                  <div className="flex justify-between">
                    <span className="text-subtle">{t('breakdown.computedWeight')}</span>
                    <span className="font-semibold text-text">{breakdown.effectiveWeightKg} kg</span>
                  </div>
                )}
                {breakdown.effectivePaletteCount != null && (
                  <div className="flex justify-between">
                    <span className="text-subtle">{t('breakdown.paletteEquiv')}</span>
                    <span className="font-semibold text-text">{breakdown.effectivePaletteCount}</span>
                  </div>
                )}
              </div>
            </section>
            <div className="h-px bg-surface-subtle" />
          </>
        )}

        {/* Demande sous la première tranche : le total ne vaut pas quantité
            demandée × prix, et sans cette ligne l'écart reste inexpliqué. */}
        {breakdown.billedAtGridMinimum && (
          <>
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">
                <span className="flex items-center gap-2">
                  {t('breakdown.gridMinimum')} <Badge variant="info">{t('breakdown.applied')}</Badge>
                </span>
              </h3>
              <div className="rounded bg-accent-soft/60 border border-accent/20 p-3.5 text-[13px] text-accent">
                {t('breakdown.gridMinimumExplain', {
                  requested: breakdown.quantity,
                  billed: breakdown.billedQuantity,
                  unit: breakdown.bandUnit || 'u',
                })}
              </div>
            </section>
            <div className="h-px bg-surface-subtle" />
          </>
        )}

        {breakdown.payantPourApplied && (
          <>
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">
                <span className="flex items-center gap-2">{t('breakdown.payantPour')} <Badge variant="info">{t('breakdown.applied')}</Badge></span>
              </h3>
              <div className="rounded bg-success-soft/60 border border-success/20 p-3.5 text-[13px] space-y-1">
                <p className="text-success">
                  {t('breakdown.nextBandBetter')}
                  <span className="font-semibold ml-1">
                    {breakdown.payantPourBandMinQty}-{breakdown.payantPourBandMaxQty ?? '+'} a {formatEUR(Number(breakdown.payantPourBandPrice))}
                    {breakdown.pricingType === 'PER_UNIT' && `/${breakdown.bandUnit || 'u'}`}
                  </span>
                </p>
                <p className="text-success text-xs">
                  {t('breakdown.billedQuantity', { billed: breakdown.billedQuantity, original: breakdown.quantity })}
                </p>
              </div>
            </section>
            <div className="h-px bg-surface-subtle" />
          </>
        )}

        {/* Cascade : d'où vient le prix, poste par poste. Le détail chiffré
            de chaque poste suit dans les sections ci-dessous. */}
        {(() => {
          const steps = [
            { label: t('breakdown.appliedSurcharges'), value: Number(breakdown.surchargeTotal ?? 0) },
            { label: t('breakdown.energySurchargeShort'), value: Number(breakdown.energySurchargeAmount ?? 0) },
            { label: t('breakdown.siteCostsShort'), value: Number(breakdown.siteCosts?.billed ?? 0) },
            { label: t('breakdown.ancillaryServices'), value: Number(breakdown.ancillaryTotal ?? 0) },
          ];
          return (
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">
                {t('breakdown.priceComposition')}
              </h3>
              <div className="rounded border border-border bg-surface-subtle/60 p-3.5">
                <PriceWaterfall
                  base={Number(breakdown.basePrice ?? 0)}
                  steps={steps}
                  total={Number(breakdown.totalPrice ?? 0)}
                  baseLabel={t('breakdown.basePrice')}
                  totalLabel={t('breakdown.total')}
                />
              </div>
            </section>
          );
        })()}

        <div className="h-px bg-surface-subtle" />

        {/* Prix de base */}
        <section>
          <h3 className="mb-3 text-[13px] font-semibold text-text">{t('breakdown.basePrice')}</h3>
          <div className="rounded bg-surface-subtle/60 border border-border p-3.5 text-[13px]">
            {breakdown.pricingType === 'PER_UNIT' ? (
              <p>{formatEUR(Number(effectiveBandPrice))} &times; {breakdown.billedQuantity} {breakdown.bandUnit || 'u'} = <span className="font-semibold">{formatEUR(breakdown.basePrice)}</span></p>
            ) : (
              <p>{t('breakdown.flatRate')} : <span className="font-semibold">{formatEUR(breakdown.basePrice)}</span></p>
            )}
          </div>
        </section>

        <div className="h-px bg-surface-subtle" />

        {/* Prix unitaire */}
        <section>
          <h3 className="mb-3 text-[13px] font-semibold text-text">{t('breakdown.unitPrice')}</h3>
          <div className="rounded bg-surface-subtle/60 border border-border p-3.5 text-[13px]">
            <div className="flex justify-between items-center">
              <span className="text-muted">{t('breakdown.pricePerUnit', { unit: breakdown.requestedUnit || 'unite' })}</span>
              <span className="font-bold text-lg text-text">{formatEUR(breakdown.pricePerUnit)}</span>
            </div>
            <p className="text-xs text-subtle mt-1">
              {formatEUR(breakdown.totalPrice)} / {breakdown.requestedQuantity} {breakdown.requestedUnit || 'u'}
            </p>
            {breakdown.purchasePrice != null && (
              <div className="mt-2.5 flex items-center gap-3 text-xs border-t border-border/60 pt-2.5">
                <span className="text-subtle">
                  {t('breakdown.purchasePerUnit')}: <span className="font-semibold text-muted">{formatEUR(breakdown.requestedQuantity > 0 ? Math.round((breakdown.purchasePrice / breakdown.requestedQuantity) * 100) / 100 : breakdown.purchasePrice)}</span>
                </span>
                <span className="h-3 w-px bg-border" />
                <span className="text-subtle">
                  {t('breakdown.salePerUnit')}: <span className="font-semibold text-muted">{formatEUR(breakdown.requestedQuantity > 0 ? Math.round(((breakdown.salesPrice ?? breakdown.purchasePrice) / breakdown.requestedQuantity) * 100) / 100 : (breakdown.salesPrice ?? breakdown.purchasePrice))}</span>
                </span>
              </div>
            )}
          </div>
        </section>

        <div className="h-px bg-surface-subtle" />

        {/* Surcharges */}
        <section>
          <h3 className="mb-3 text-[13px] font-semibold text-text">
            {t('breakdown.appliedSurcharges')}
            {breakdown.surcharges?.length > 0 && (
              <span className="ml-2 text-2xs font-normal text-subtle">({breakdown.surcharges.length})</span>
            )}
          </h3>
          {!breakdown.surcharges || breakdown.surcharges.length === 0 ? (
            <p className="text-[13px] text-subtle">{t('breakdown.noSurcharges')}</p>
          ) : (
            <div className="space-y-2">
              {breakdown.surcharges.map((s, i) => {
                const amt = s.ruleAmount ?? s.amount;
                const computed = s.computedAmount ?? s.computed;
                return (
                  <div key={i} className="rounded border border-border/80 bg-surface p-3.5 text-[13px]">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-text">{s.ruleName}</span>
                      <span className="font-semibold text-text">{formatEUR(computed)}</span>
                    </div>
                    <p className="mt-1 text-xs text-subtle">
                      {s.surchargeType === 'PERCENT'
                        ? (s.surchargeBase === 'BASE' ? t('breakdown.percentOnBase', { amount: amt }) : t('breakdown.percentOnSubtotal', { amount: amt }))
                        : s.surchargeType === 'PER_UNIT'
                          ? t('breakdown.perUnitAmount', { amount: formatEUR(amt) })
                          : (s.surchargeBase === 'BASE' ? t('breakdown.fixedOnBase', { amount: formatEUR(amt) }) : t('breakdown.fixedOnSubtotal', { amount: formatEUR(amt) }))}
                    </p>
                  </div>
                );
              })}
              <div className="flex justify-between rounded bg-surface-subtle/60 border border-border p-3.5 text-[13px] font-semibold">
                <span>{t('breakdown.totalSurcharges')}</span>
                <span>{formatEUR(breakdown.surchargeTotal)}</span>
              </div>
            </div>
          )}
        </section>

        {/* Logistics site costs */}
        {breakdown.siteCosts && (
          <>
            <div className="h-px bg-surface-subtle" />
            <SiteCostsSection siteCosts={breakdown.siteCosts} />
          </>
        )}

        {/* Energy surcharge (loi du 5 janvier 2006) */}
        {breakdown.energySurcharge && (
          <>
            <div className="h-px bg-surface-subtle" />
            <EnergySurchargeSection breakdown={breakdown.energySurcharge} amount={breakdown.energySurchargeAmount} />
          </>
        )}

        {/* CATEGORIZED ANCILLARY SECTIONS */}
        {hasCategories ? (
          <>
            {appliedCharges.length > 0 && (
              <>
                <div className="h-px bg-surface-subtle" />
                <section>
                  <h3 className="mb-3 text-[13px] font-semibold text-text flex items-center gap-2">
                    <Check className="h-4 w-4 text-success" />
                    {t('breakdown.appliedToQuote')}
                    <span className="text-2xs font-normal text-subtle">({appliedCharges.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {appliedCharges.map((a, i) => (
                      <div key={i} className="rounded border border-success/20 bg-success-soft/30 p-3.5 text-[13px]">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-text">{a.ruleName}</span>
                          <span className="font-semibold text-success">+{formatEUR(a.delta)}</span>
                        </div>
                        <p className="mt-1 text-xs text-subtle">{a.reason}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}

            {includedItems.length > 0 && (
              <>
                <div className="h-px bg-surface-subtle" />
                <section>
                  <h3 className="mb-3 text-[13px] font-semibold text-text flex items-center gap-2">
                    <Info className="h-4 w-4 text-accent" />
                    {t('breakdown.includedInPrice')}
                  </h3>
                  <div className="space-y-2">
                    {includedItems.map((a, i) => (
                      <div key={i} className="rounded border border-accent/20 bg-accent-soft/30 p-3.5 text-[13px]">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-muted">{a.ruleName}</span>
                          <Badge variant="success">{t('breakdown.included')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}

            {breakdown.ancillaryTotal !== 0 && (
              <div className="flex justify-between rounded bg-surface-subtle/60 border border-border p-3.5 text-[13px] font-semibold">
                <span>{t('breakdown.totalPrestations')}</span>
                <span>{formatEUR(breakdown.ancillaryTotal)}</span>
              </div>
            )}

            {potentialEvents.length > 0 && (
              <>
                <div className="h-px bg-surface-subtle" />
                <section>
                  <h3 className="mb-3 text-[13px] font-semibold text-text flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-warning" />
                    {t('breakdown.potentialEvents')}
                  </h3>
                  <p className="text-xs text-subtle mb-3">
                    {t('breakdown.potentialEventsHelp')}
                  </p>
                  <div className="space-y-2">
                    {potentialEvents.map((a, i) => (
                      <div key={i} className="rounded border border-warning/20 bg-warning-soft/30 p-3.5 text-[13px]">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-warning">{a.ruleName}</span>
                          <Badge variant="warning">{t('breakdown.event')}</Badge>
                        </div>
                        {a.calculation && (
                          <p className="mt-1 text-xs text-warning">
                            {a.calculation.calc_type === 'FIXED' && `${formatEUR(Number(a.calculation.amount))} fixe`}
                            {a.calculation.calc_type === 'PERCENT' && `${formatPercent((a.calculation.rate || 0) * 100, { digits: 0 })} du prix`}
                            {a.calculation.calc_type === 'PER_UNIT' && `${formatEUR(Number(a.calculation.amount))}/${a.calculation.per_unit === 'KG' ? 'kg' : 'exp'}`}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}

            {invoiceItems.length > 0 && (
              <>
                <div className="h-px bg-surface-subtle" />
                <section>
                  <h3 className="mb-3 text-[13px] font-semibold text-text flex items-center gap-2">
                    <FileText className="h-4 w-4 text-subtle" />
                    {t('breakdown.invoiceFees')}
                  </h3>
                  <div className="space-y-2">
                    {invoiceItems.map((a, i) => (
                      <div key={i} className="rounded border border-border bg-surface-subtle/50 p-3.5 text-[13px] opacity-70">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-muted">{a.ruleName}</span>
                          <Badge variant="neutral">{t('breakdown.invoice')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}

            {(notAppliedInfo.length > 0 || missingInput.length > 0) && (
              <>
                <div className="h-px bg-surface-subtle" />
                <section>
                  <h3 className="mb-3 text-[13px] font-semibold text-text flex items-center gap-2">
                    <Clock className="h-4 w-4 text-subtle" />
                    {t('breakdown.notCalculated')}
                  </h3>
                  <div className="space-y-2">
                    {notAppliedInfo.map((a, i) => (
                      <div key={i} className="rounded border border-accent/20 bg-accent-soft/40 p-3.5 text-[13px]">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-subtle">{a.ruleName}</span>
                          <Badge variant="info">{t('breakdown.onQuote')}</Badge>
                        </div>
                      </div>
                    ))}
                    {missingInput.map((a, i) => (
                      <div key={`mi-${i}`} className="rounded border border-border bg-surface-subtle/50 p-3.5 text-[13px] opacity-60">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-subtle">{a.ruleName}</span>
                          <Badge variant="neutral">{t('breakdown.missingInput')}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-subtle">{a.reason}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}
          </>
        ) : flatAncillaries.length > 0 ? (
          <>
            <div className="h-px bg-surface-subtle" />
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">
                {t('breakdown.ancillaryServices')}
                <span className="ml-2 text-2xs font-normal text-subtle">
                  ({t('breakdown.appliedCount', { count: flatAncillaries.filter((a) => a.applicationStatus === 'APPLIED').length })})
                </span>
              </h3>
              <div className="space-y-2">
                {flatAncillaries.map((a, i) => {
                  const isApplied = a.applicationStatus === 'APPLIED';
                  const isInfo = a.applicationStatus === 'NOT_APPLIED_INFO';
                  return (
                    <div key={i} className={`rounded border p-3.5 text-[13px] ${isApplied ? 'border-border/80 bg-surface' : isInfo ? 'border-accent/20 bg-accent-soft/40' : 'border-border bg-surface-subtle/50 opacity-60'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isApplied ? 'text-text' : 'text-subtle'}`}>{a.ruleName}</span>
                        {isApplied && a.delta !== 0 ? (
                          <span className="font-semibold text-text">{a.delta > 0 ? '+' : ''}{formatEUR(a.delta)}</span>
                        ) : isApplied ? (
                          <Badge variant="success">{t('breakdown.included')}</Badge>
                        ) : isInfo ? (
                          <Badge variant="info">{t('breakdown.onQuote')}</Badge>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-subtle">{a.reason}</p>
                    </div>
                  );
                })}
                {breakdown.ancillaryTotal !== 0 && (
                  <div className="flex justify-between rounded bg-surface-subtle/60 border border-border p-3.5 text-[13px] font-semibold">
                    <span>{t('breakdown.totalPrestations')}</span>
                    <span>{formatEUR(breakdown.ancillaryTotal)}</span>
                  </div>
                )}
              </div>
            </section>
          </>
        ) : null}

        {/* Total */}
        <div className="flex justify-between rounded bg-gradient-to-r from-accent to-accent-hover p-5 text-white font-semibold shadow-elevated">
          <span>{t('breakdown.total')}</span>
          <span className="text-lg">{formatEUR(breakdown.totalPrice)}</span>
        </div>

        {/* Warnings */}
        {breakdown.warnings && breakdown.warnings.length > 0 && (
          <>
            <div className="h-px bg-surface-subtle" />
            <section>
              <h3 className="mb-3 text-[13px] font-semibold text-text">{t('breakdown.warnings')}</h3>
              <div className="space-y-2">
                {breakdown.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2.5 rounded bg-warning-soft/60 border border-warning/20 p-3.5 text-[13px]">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />
                    <p className="text-warning">{typeof w === 'string' ? w : w.message}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {isFallback && (
          <>
            <div className="h-px bg-surface-subtle" />
            <div className="rounded bg-warning-soft/60 border border-warning/20 p-3.5 text-[13px] text-warning">
              {t('breakdown.fallbackNotice', { type: breakdown.laneFallback })}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

function EnergySurchargeSection({
  breakdown,
  amount,
}: {
  breakdown: EnergySurchargeBreakdown;
  amount: number;
}) {
  const { t } = useTranslation();
  const fmtPct = (v: number) => formatPercent(v * 100, { digits: 2 });
  const fmtSignedPct = (v: number) => formatPercent(v * 100, { digits: 2, signed: true });
  const isSuppressed =
    breakdown.thresholdSuppressed ||
    breakdown.decreaseSuppressed ||
    breakdown.spotSuppressed;

  return (
    <section>
      <h3 className="mb-3 text-[13px] font-semibold text-text flex items-center gap-2">
        <Info className="h-4 w-4 text-accent" />
        {t('breakdown.energySurcharge')}
        <span className="ml-auto font-semibold">
          {fmtSignedPct(breakdown.totalRatePct)} = {amount >= 0 ? '+' : ''}
          {formatEUR(amount)}
        </span>
      </h3>
      <p className="text-xs text-subtle mb-3">{t('breakdown.energySurchargeHelp')}</p>

      <div className="rounded border border-border/80 bg-surface p-3.5 text-[13px] space-y-2.5">
        {breakdown.lines.map((line, i) => {
          const missing = line.warning?.startsWith('INDEX_MISSING');
          return (
            <div
              key={i}
              className={`flex flex-col gap-1 ${missing ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-text">
                  {line.energyTypeLabel}
                  <span className="ml-2 text-subtle font-normal">
                    {formatPercent(line.fleetMixPct * 100, { digits: 0 })} · {line.indexSourceCode} · M-{line.lagMonths}
                  </span>
                </span>
                <span className="font-mono text-xs text-text">
                  {missing ? '—' : fmtSignedPct(line.ratePct)}
                </span>
              </div>
              {!missing && (
                <p className="text-2xs text-subtle font-mono">
                  {Number(line.refIndexValue).toFixed(4)} → {Number(line.currentIndexValue).toFixed(4)} (
                  {fmtSignedPct(line.deltaPct)})
                </p>
              )}
              {missing && (
                <p className="text-2xs text-warning">
                  {t('breakdown.energyMissingIndex', { source: line.indexSourceCode })}
                </p>
              )}
            </div>
          );
        })}

        <div className="border-t border-border pt-2.5 flex justify-between text-xs">
          <span className="text-subtle">{t('breakdown.energyTotalRate')}</span>
          <span className="font-semibold text-text">
            {fmtSignedPct(breakdown.totalRatePct)}
          </span>
        </div>

        {isSuppressed && breakdown.totalRatePct !== breakdown.rawTotalRatePct && (
          <div className="flex justify-between text-2xs text-subtle">
            <span>{t('breakdown.energyRawRate')}</span>
            <span className="font-mono">{fmtSignedPct(breakdown.rawTotalRatePct)}</span>
          </div>
        )}

        {breakdown.thresholdSuppressed && (
          <div className="rounded bg-warning-soft/40 border border-warning/20 p-2 text-xs text-warning">
            {t('breakdown.energyThresholdSuppressed')}
            {breakdown.triggerThreshold != null && ` (< ${fmtPct(breakdown.triggerThreshold)})`}
          </div>
        )}
        {breakdown.decreaseSuppressed && (
          <div className="rounded bg-warning-soft/40 border border-warning/20 p-2 text-2xs text-warning">
            {t('breakdown.energyDecreaseSuppressed')}
          </div>
        )}
        {breakdown.spotSuppressed && (
          <div className="rounded bg-warning-soft/40 border border-warning/20 p-2 text-xs text-warning">
            {t('breakdown.energySpotSuppressed')}
          </div>
        )}

        {breakdown.invoiceFooter && (
          <div className="rounded bg-surface-subtle/60 border border-border p-2.5 text-2xs text-subtle italic">
            <p className="text-2xs uppercase tracking-wide font-semibold mb-1 text-subtle/80">
              {t('breakdown.energyInvoiceFooter')}
            </p>
            {breakdown.invoiceFooter}
          </div>
        )}
      </div>
    </section>
  );
}

function SiteCostsSection({ siteCosts }: { siteCosts: PricingSiteCosts }) {
  const { t } = useTranslation();
  const marginsEnabled = useMarginsEnabled();
  const items = siteCosts.items || [];
  const marginPct = Number(siteCosts.marginPct ?? 0);
  const total = Number(siteCosts.total ?? 0);
  const billed = Number(siteCosts.billed ?? 0);
  const marginAmount = Math.round((billed - total) * 100) / 100;

  const billingUnitLabel: Record<string, string> = {
    forfait: t('breakdown.billingUnit.flat'),
    palette: t('breakdown.billingUnit.perPalette'),
    pal: t('breakdown.billingUnit.perPalette'),
    colis: t('breakdown.billingUnit.perParcel'),
    bouteille: t('breakdown.billingUnit.perBottle'),
    kg: t('breakdown.billingUnit.perKg'),
    t: t('breakdown.billingUnit.perTonne'),
    pcs: t('breakdown.billingUnit.perPiece'),
    m3: t('breakdown.billingUnit.perM3'),
    // Legacy aliases (data created before unit catalogue alignment)
    FIXED: t('breakdown.billingUnit.flat'),
    PER_PALETTE: t('breakdown.billingUnit.perPalette'),
    PER_PARCEL: t('breakdown.billingUnit.perParcel'),
    PER_KG: t('breakdown.billingUnit.perKg'),
  };

  return (
    <section>
      <h3 className="mb-3 text-[13px] font-semibold text-text">
        {t('breakdown.siteCosts')}
        <span className="ml-2 text-2xs font-normal text-subtle">
          {siteCosts.siteName} · {t('breakdown.siteCostsDownstream')}
        </span>
      </h3>
      {items.length === 0 ? (
        <p className="text-[13px] text-subtle">{t('breakdown.noSiteCosts')}</p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex items-start justify-between gap-3 rounded border border-border bg-surface-subtle/40 px-3 py-2 text-[13px]"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-text truncate">{it.name}</p>
                <p className="text-2xs text-subtle">
                  {formatEUR(Number(it.unitAmount))} {billingUnitLabel[it.billingUnit] || it.billingUnit}
                  {it.billingUnit !== 'FIXED' && ` × ${Number(it.quantity)}`}
                </p>
              </div>
              <span className="font-mono tabular-nums text-text">{formatEUR(Number(it.amount))}</span>
            </div>
          ))}
          <div className="flex justify-between text-xs text-subtle pt-1">
            <span>{t('breakdown.siteCostsSubtotal')}</span>
            <span className="font-mono tabular-nums">{formatEUR(total)}</span>
          </div>
          {marginsEnabled && marginPct > 0 && (
            <div className="flex justify-between text-xs text-subtle">
              <span>{t('breakdown.siteCostsMargin', { pct: formatNumber(marginPct, { digits: 2 }) })}</span>
              <span className="font-mono tabular-nums">+ {formatEUR(marginAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-[13px] font-semibold pt-1 border-t border-border">
            <span>{t('breakdown.siteCostsBilled')}</span>
            <span className="font-mono tabular-nums">{formatEUR(billed)}</span>
          </div>
        </div>
      )}
    </section>
  );
}
